import React, { useState, useEffect } from 'react';
import { API_CONFIG } from '../../config/api';
import { HotTopicDTO, HotTopicCreateRequest, HotTopicUpdateRequest } from '../../types/topic';
import './HotTopicsManagement.css';

const HotTopicsManagement = () => {
  const [hotTopics, setHotTopics] = useState<HotTopicDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingTopic, setEditingTopic] = useState<HotTopicDTO | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState<HotTopicCreateRequest>({
    topic: '',
    iconUrl: '',
    searchQuery: '',
    countryCode: '',
    isGlobal: false,
    isActive: true,
  });

  useEffect(() => {
    fetchHotTopics();
  }, []);

  const fetchHotTopics = async () => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.HOT_TOPICS.BASE}`);
      if (!response.ok) {
        throw new Error('Failed to fetch hot topics');
      }
      const data = await response.json();

      // Handle different possible response formats
      if (Array.isArray(data)) {
        setHotTopics(data);
      } else if (data.hotTopics && Array.isArray(data.hotTopics)) {
        setHotTopics(data.hotTopics);
      } else if (data.data && Array.isArray(data.data)) {
        setHotTopics(data.data);
      } else {
        console.error('Unexpected response format:', data);
        setError('Invalid response format from server');
      }
    } catch (err) {
      setError('Failed to load hot topics');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCreate = async () => {
    try {
      const requestData = {
        ...formData,
        countryCode: formData.countryCode || undefined
      };

      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.HOT_TOPICS.CREATE}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        throw new Error('Failed to create hot topic');
      }

      await fetchHotTopics();
      setShowCreateForm(false);
      setFormData({
        topic: '',
        iconUrl: '',
        searchQuery: '',
        countryCode: '',
        isGlobal: false,
        isActive: true,
      });
    } catch (err) {
      setError('Failed to create hot topic');
      console.error('Error:', err);
    }
  };

  const handleEdit = (topic: HotTopicDTO) => {
    setEditingTopic(topic);
    setFormData({
      topic: topic.topic,
      iconUrl: topic.iconUrl || '',
      searchQuery: topic.searchQuery || '',
      countryCode: topic.countryCode || '',
      isGlobal: topic.isGlobal,
      isActive: topic.isActive,
    });
  };

  const handleUpdate = async () => {
    if (!editingTopic) return;

    try {
      const requestData: HotTopicUpdateRequest = {
        id: editingTopic.id,
        topic: formData.topic,
        iconUrl: formData.iconUrl,
        searchQuery: formData.searchQuery,
        countryCode: formData.countryCode || undefined,
        isGlobal: formData.isGlobal,
        isActive: formData.isActive,
      };

      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.HOT_TOPICS.UPDATE(editingTopic.id)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        throw new Error('Failed to update hot topic');
      }

      await fetchHotTopics();
      setEditingTopic(null);
      setFormData({
        topic: '',
        iconUrl: '',
        searchQuery: '',
        countryCode: '',
        isGlobal: false,
        isActive: true,
      });
    } catch (err) {
      setError('Failed to update hot topic');
      console.error('Error:', err);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this hot topic?')) {
      return;
    }

    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.HOT_TOPICS.DELETE(id)}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete hot topic');
      }

      await fetchHotTopics();
    } catch (err) {
      setError('Failed to delete hot topic');
      console.error('Error:', err);
    }
  };

  const handleToggleActive = async (topic: HotTopicDTO) => {
    try {
      const updatedTopic: HotTopicUpdateRequest = {
        id: topic.id,
        topic: topic.topic,
        iconUrl: topic.iconUrl,
        searchQuery: topic.searchQuery,
        countryCode: topic.countryCode || undefined,
        isGlobal: topic.isGlobal,
        isActive: !topic.isActive,
      };

      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.HOT_TOPICS.UPDATE(topic.id)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedTopic),
      });

      if (!response.ok) {
        throw new Error('Failed to toggle hot topic status');
      }

      await fetchHotTopics();
    } catch (err) {
      setError('Failed to toggle hot topic status');
      console.error('Error:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      topic: '',
      iconUrl: '',
      searchQuery: '',
      countryCode: '',
      isGlobal: false,
      isActive: true,
    });
    setShowCreateForm(false);
    setEditingTopic(null);
  };

  if (loading) {
    return <div className="loading">Loading hot topics...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="hot-topics-management">
      <div className="hot-topics-header">
        <div>
          <h2>Hot Topics Management</h2>
          <p className="hot-topics-description">Hot topic means it will be visible in the app as bottom navigation.</p>
        </div>
        <button
          className="create-button"
          onClick={() => setShowCreateForm(true)}
        >
          Add New Hot Topic
        </button>
      </div>

      <div className="hot-topics-list">
        {hotTopics.length === 0 ? (
          <div className="no-topics">No hot topics found</div>
        ) : (
          hotTopics.map(topic => (
            <div key={topic.id} className="hot-topic-card">
              <div className="topic-info">
                {topic.iconUrl && (
                  <div className="topic-icon">
                    <img src={topic.iconUrl} alt={topic.topic} />
                  </div>
                )}
                <h3>{topic.topic}</h3>
                <div className="topic-details">
                  <span className={`scope ${topic.isGlobal ? 'global' : 'country'}`}>
                    {topic.isGlobal ? 'Global' : 'Country'}
                  </span>
                  {!topic.isGlobal && topic.countryCode && (
                    <span className="country-code">{topic.countryCode}</span>
                  )}
                  <span className={`status ${topic.isActive ? 'active' : 'inactive'}`}>
                    {topic.isActive ? 'Active' : 'Inactive'}
                  </span>
                  {topic.searchQuery && (
                    <span className="search-query">
                      Query: {topic.searchQuery}
                    </span>
                  )}
                </div>
              </div>
              <div className="topic-actions">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={topic.isActive}
                    onChange={() => handleToggleActive(topic)}
                  />
                  <span className="slider"></span>
                </label>
                <button
                  className="edit-button"
                  onClick={() => handleEdit(topic)}
                >
                  Edit
                </button>
                <button
                  className="delete-button"
                  onClick={() => handleDelete(topic.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {(showCreateForm || editingTopic) && (
        <div className="modal">
          <div className="modal-content">
            <h3>{editingTopic ? 'Edit Hot Topic' : 'Create New Hot Topic'}</h3>

            <div className="form-group">
              <label htmlFor="topic">Topic Name</label>
              <input
                type="text"
                id="topic"
                name="topic"
                value={formData.topic}
                onChange={handleInputChange}
                placeholder="Enter topic name"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="iconUrl">Icon URL</label>
              <input
                type="text"
                id="iconUrl"
                name="iconUrl"
                value={formData.iconUrl}
                onChange={handleInputChange}
                placeholder="Enter icon URL"
              />
            </div>

            <div className="form-group">
              <label htmlFor="searchQuery">Search Query</label>
              <input
                type="text"
                id="searchQuery"
                name="searchQuery"
                value={formData.searchQuery}
                onChange={handleInputChange}
                placeholder="Enter search query"
              />
            </div>

            <div className="form-group">
              <label htmlFor="isGlobal">Scope</label>
              <select
                id="isGlobal"
                name="isGlobal"
                value={formData.isGlobal.toString()}
                onChange={handleInputChange}
              >
                <option value="false">Country Specific</option>
                <option value="true">Global</option>
              </select>
            </div>

            {!formData.isGlobal && (
              <div className="form-group">
                <label htmlFor="countryCode">Country Code</label>
                <input
                  type="text"
                  id="countryCode"
                  name="countryCode"
                  value={formData.countryCode}
                  onChange={handleInputChange}
                  placeholder="e.g., US, DE, FR"
                />
              </div>
            )}

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                />
                Active
              </label>
            </div>

            <div className="modal-actions">
              <button
                className="save-button"
                onClick={editingTopic ? handleUpdate : handleCreate}
              >
                {editingTopic ? 'Update' : 'Create'}
              </button>
              <button
                className="cancel-button"
                onClick={resetForm}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HotTopicsManagement;